// Relative path: src/components/Recommendations/HookRow.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import recUtils from '../../utils/RecUtils';

// Import profile pictures for random selection
import pp1 from  '../../images/profilePictures/pp1.webp';
import pp2 from  '../../images/profilePictures/pp2.webp';
import pp3 from  '../../images/profilePictures/pp3.webp';
import pp4 from  '../../images/profilePictures/pp4.webp';
import pp5 from  '../../images/profilePictures/pp5.webp';
import pp6 from  '../../images/profilePictures/pp6.webp';
import pp7 from  '../../images/profilePictures/pp7.webp';
import pp8 from  '../../images/profilePictures/pp8.webp';
import pp9 from  '../../images/profilePictures/pp9.webp';
import pp10 from '../../images/profilePictures/pp10.webp';
import pp11 from '../../images/profilePictures/pp11.webp';
import pp12 from '../../images/profilePictures/pp12.webp';
import pp13 from '../../images/profilePictures/pp13.webp';
import pp14 from '../../images/profilePictures/pp14.webp';
import pp15 from '../../images/profilePictures/pp15.webp';
import pp16 from '../../images/profilePictures/pp16.webp';
import pp17 from '../../images/profilePictures/pp17.webp';
import pp18 from '../../images/profilePictures/pp18.webp';
import pp19 from '../../images/profilePictures/pp19.webp';
import pp20 from '../../images/profilePictures/pp20.webp';
import pp21 from '../../images/profilePictures/pp21.webp';
import pp22 from '../../images/profilePictures/pp22.webp';
import pp23 from '../../images/profilePictures/pp23.webp';
import pp24 from '../../images/profilePictures/pp24.webp';
import pp25 from '../../images/profilePictures/pp25.webp';
import pp26 from '../../images/profilePictures/pp26.webp';
import pp27 from '../../images/profilePictures/pp27.webp';
import pp28 from '../../images/profilePictures/pp28.webp';

/**
 * Optimized Profile Picture Component
 * Only applies GPU acceleration during active animation to save memory
 */
const OptimizedProfilePicture = React.memo(({ 
  profilePic, 
  index, 
  shouldAnimate, 
  animationClasses = '',
  alt = `Patient ${index + 1}` 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  // Track animation lifecycle to manage GPU acceleration
  useEffect(() => {
    if (shouldAnimate && !isAnimating) {
      setIsAnimating(true);
      
      // Remove GPU acceleration after animation completes
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 800); // Animation duration + buffer
      
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate, isAnimating]);

  return (
    <div
      className={`w-12 h-12 rounded-full overflow-hidden border-2 border-white relative ${animationClasses}`}
      style={{
        marginLeft: index === 0 ? 0 : -8, 
        zIndex: 5 - index,
        boxShadow: '0 3px 6px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)',
        // Only apply GPU acceleration during animation
        ...(isAnimating && { 
          willChange: 'transform',
          transform: 'translateZ(0)' 
        })
      }}
    >
      <img
        src={profilePic}
        alt={alt}
        className="w-full h-full object-cover"
      />
    </div>
  );
});

const HookRow = ({
  option,
  isActive = true,
  optionIndex = 0,
  scrollContainerRef = null,
  allowAnimations = true, // New prop to control when animations can start
}) => {
  // All available profile pictures
  const profilePictures = [
    pp1, pp2, pp3, pp4, pp5,
    pp6, pp7, pp8, pp9, pp10,
    pp11, pp12, pp13, pp14,
    pp15, pp16, pp17, pp18, 
    pp19, pp20, pp21, pp22,
    pp23, pp24, pp25, pp26,
    pp27, pp28
  ];

  const [hookData, setHookData] = useState({
    hookText: 'Many patients similar to you loved this clinic',
    hookSubtext: '1000+ Verified Reviewers',
    userToggle1: null,
    userToggle2: null
  });
  const [isLoading, setIsLoading] = useState(true);

  // Animation flags - simplified without confetti
  const [hasAnimated, setHasAnimated] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const hookRowRef = useRef(null);

  // Stable seed for randomization
  const stableId = `${option?.title || 'default'}-${optionIndex}`;

  // Deterministic random profile pics - memoized for performance
  const randomProfiles = useMemo(() => {
    const getNumericHash = (str) => {
      let h = 0;
      for (let i = 0; i < str.length; i++) {
        h = ((h << 5) - h) + str.charCodeAt(i);
        h |= 0;
      }
      return Math.abs(h);
    };
    const seed = getNumericHash(stableId);
    const seededRandom = (max) => {
      const x = Math.sin(seed + 1) * 10000;
      return Math.floor((x - Math.floor(x)) * max);
    };
    const shuffled = [...profilePictures];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = seededRandom(i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 5);
  }, [stableId, profilePictures]);

  // IntersectionObserver for flip - only start if allowAnimations is true
  useEffect(() => {
    const el = hookRowRef.current;
    if (!el || hasAnimated || isLoading || !isActive || !allowAnimations) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated && isActive && allowAnimations) {
          setShouldAnimate(true);
          setHasAnimated(true);
          setTimeout(() => observer.unobserve(entry.target), 1000);
        }
      },
      { threshold: 0.3, rootMargin: '0px 0px -20px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated, isLoading, isActive, allowAnimations]);

  // Fallback trigger - only start if allowAnimations is true
  useEffect(() => {
    if (!isLoading && !hasAnimated && isActive && allowAnimations) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
        setHasAnimated(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, hasAnimated, isActive, allowAnimations]);

  // Load hook texts
  useEffect(() => {
    const load = async () => {
      if (!option?.title) {
        setIsLoading(false);
        return;
      }
      try {
        const ht = await recUtils.getHookTexts(option.title);
        if (ht) {
          setHookData({
            hookText: ht.hookText,
            hookSubtext: ht.hookSubtext,
            userToggle1: ht.toggles?.userToggle1 || null,
            userToggle2: ht.toggles?.userToggle2 || null
          });
        }
      } catch (error) {
        console.error('Error fetching hook texts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [option]);

  // Checkmark icon
  const CheckmarkIcon = () => (
    <svg
      className="w-5 h-5 text-gray-600 mr-2 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );

  // Scroll into view logic with mobile-only offset
  const scrollToAiCommentary = () => {
    const commentaryId = `ai-commentary-${option.title.replace(/\s+/g, '-').toLowerCase()}`;
    const commentaryElement = document.getElementById(commentaryId);

    if (scrollContainerRef?.current && commentaryElement) {
      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elemRect = commentaryElement.getBoundingClientRect();
      const baseOffset = elemRect.top - containerRect.top + container.scrollTop;
      const isMobile = window.innerWidth < 768;
      const offset = isMobile ? 60 : 20;
      container.scrollTo({
        top: baseOffset - offset,
        behavior: 'smooth'
      });
    } else if (commentaryElement) {
      const position = commentaryElement.getBoundingClientRect().top + window.pageYOffset;
      const isMobile = window.innerWidth < 768;
      const offset = isMobile ? 60 : 20;
      window.scrollTo({
        top: position - offset,
        behavior: 'smooth'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="px-5 mt-1 md:mt-0 md:px-0 md:h-full md:flex md:items-center">
        <div className="bg-white rounded-lg w-full flex items-center px-4 py-2 text-white md:h-full">
          <div className="flex-1">
            <div className="animate-pulse h-4 bg-green-400 bg-opacity-40 rounded w-3/4 mb-2 mx-auto" />
            <div className="animate-pulse h-3 bg-green-400 bg-opacity-40 rounded w-1/2 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={hookRowRef}
      className="px-5 mt-1 md:mt-0 md:px-0 md:h-full md:flex md:items-center"
    >
      <div
        className=" rounded-lg w-full px-4 py-2 border border-gray-200 bg-white shadow-gray-300 shadow-md text-gray-600 md:h-full md:flex md:flex-col md:justify-center cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
        onClick={scrollToAiCommentary}
        role="button"
        aria-label="View patient reviews"
      >
        <div className="flex justify-center items-center py-2">
          <div className="flex items-center">
            {randomProfiles.map((profilePic, index) => {
              const animationClasses = shouldAnimate ? `flip-vertical-right flip-delay-${index}` : '';
              return (
                <OptimizedProfilePicture
                  key={index}
                  profilePic={profilePic}
                  index={index}
                  shouldAnimate={shouldAnimate}
                  animationClasses={animationClasses}
                  alt={`Patient ${index + 1}`}
                />
              );
            })}

            {/* Plus icon at end - no GPU acceleration needed */}
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ marginLeft: -8, zIndex: 0 }}
            >
              <svg
                className="w-8 h-8 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M12 6v12M6 12h12"
                />
              </svg>
            </div>
          </div>
        </div>
        <div className="flex-1 text-center">
          <p className="font-semibold text-base md:leading-[22px] mb-2">
            {hookData.hookText}
          </p>
          <div className="mt-2 flex flex-col items-center">
            {hookData.userToggle1 && (
              <div className="bg-blue-100 p-1 px-3 rounded-2xl flex items-center mb-1 justify-center">
                <CheckmarkIcon />
                <p className="text-sm md:leading-[22px]">{hookData.userToggle1}</p>
              </div>
            )}
            {hookData.userToggle2 && (
              <div className="bg-blue-100 p-1 px-3 rounded-2xl flex items-center mb-1 justify-center">
                <CheckmarkIcon />
                <p className="text-sm md:leading-[22px]">{hookData.userToggle2}</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default HookRow;